import type { ObjectTag } from '@atlas/types'
import { ListSkeleton } from '@atlas/ui-components'
import { Chip, type ComboboxOption } from '@atlas/ui-primitives'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/editorial/PageHeader'
import { useClassifyBatch } from '@/hooks/useClassification'
import { useAxesWithValues } from '@/hooks/useLibrary'
import { useCreateMessage, useMessage, useUpdateMessage } from '@/hooks/useMessages'
import { useWorkOrders } from '@/hooks/useWorkOrders'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import {
	partitionTags,
	toMessageTuples,
	useMessageAttributes,
	useMessageClassification,
	useSetMessageOrderCodes,
} from './classification'
import { EMPTY_FORM, fromMessage, type MessageFormState, toCreateInput, toUpdateInput } from './form-model'
import { MessageForm } from './MessageForm'

const useSet =
	(setForm: React.Dispatch<React.SetStateAction<MessageFormState>>) =>
	<K extends keyof MessageFormState>(key: K, value: MessageFormState[K]) =>
		setForm((f) => ({ ...f, [key]: value }))

/**
 * The work orders the Work orders field offers. Labelled `code · title` because the
 * CODE is what is stored on the message and what the order's Messages section reads
 * back — the title alone would hide which value the link actually carries.
 */
function useOrderOptions(): ComboboxOption[] {
	const { data: workOrders = [] } = useWorkOrders()
	return workOrders.map((o) => ({ value: o.orderCode, label: `${o.orderCode} · ${o.title}` }))
}

/**
 * The two writes that follow a message save, in order: its classification tuple, then
 * its `order_code` links. Neither is part of the message record itself, and a message
 * whose descriptors did not land is not saved as far as any surface that reads by them
 * is concerned — so a failure is reported and the page stays put instead of navigating
 * away from work the user would have to redo blind.
 */
async function saveDescriptors(
	messageId: string,
	form: MessageFormState,
	classify: ReturnType<typeof useClassifyBatch>,
	setOrderCodes: ReturnType<typeof useSetMessageOrderCodes>,
	{ replaceOrderCodes }: { replaceOrderCodes: boolean },
): Promise<boolean> {
	try {
		const tuples = toMessageTuples(messageId, form.classification)
		if (tuples.length > 0) await classify.mutateAsync(tuples)
		// On create there is nothing to clear, so an empty set is a no-op; on edit an
		// empty set IS the change — it unlinks the message from every order.
		if (replaceOrderCodes || form.orderCodes.length > 0)
			await setOrderCodes.mutateAsync({ messageId, orderCodes: form.orderCodes })
		return true
	} catch (error) {
		notification.error(apiErrorMessage(error, 'The message was saved, but its classification and links were not.'))
		return false
	}
}

/** New Message — full-page form (route `/messages/new`). */
export function NewMessagePage() {
	const navigate = useNavigate()
	const create = useCreateMessage()
	const classify = useClassifyBatch()
	const setOrderCodes = useSetMessageOrderCodes()
	const { axes } = useAxesWithValues()
	const orderOptions = useOrderOptions()
	const [form, setForm] = useState<MessageFormState>(EMPTY_FORM)
	const set = useSet(setForm)

	const onSubmit = () =>
		create.mutate(toCreateInput(form), {
			onSuccess: (created) => {
				void saveDescriptors(created.messageId, form, classify, setOrderCodes, { replaceOrderCodes: false }).then(
					(ok) => {
						if (ok) void navigate('/messages')
					},
				)
			},
		})

	return (
		<div>
			<Link
				to="/messages"
				className="mb-4 inline-flex items-center gap-1 text-sm text-ds-text-muted hover:text-ds-text"
			>
				<ArrowLeft className="h-4 w-4" /> Messages
			</Link>
			<PageHeader
				eyebrow="Messages / New"
				title="New message"
				subtitle="A unit of copy — tagline, copy-in/out, CTA, or stage."
			/>
			<div className="mt-6">
				<MessageForm
					form={form}
					set={set}
					axes={axes}
					orderOptions={orderOptions}
					submitLabel="Create message"
					isPending={create.isPending || classify.isPending || setOrderCodes.isPending}
					onSubmit={onSubmit}
					onCancel={() => void navigate('/messages')}
				/>
			</div>
		</div>
	)
}

/** Edit Message — same form hydrated from an existing message. */
export function EditMessagePage() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { data: message, isLoading } = useMessage(id ?? null)
	const { data: tags, isLoading: isClassLoading } = useMessageClassification(id ?? null)
	const { data: objectAttributes, isLoading: isAttrLoading } = useMessageAttributes(id ?? null)
	const update = useUpdateMessage()
	const classify = useClassifyBatch()
	const setOrderCodes = useSetMessageOrderCodes()
	const orderOptions = useOrderOptions()
	const { axes, isLoading: isAxesLoading } = useAxesWithValues()
	const [form, setForm] = useState<MessageFormState | null>(null)
	const [attributes, setAttributes] = useState<ObjectTag[]>([])
	const set = useSet(setForm as React.Dispatch<React.SetStateAction<MessageFormState>>)

	// Seed once the message AND its tags AND the axis registry have all loaded. Axes
	// are required to partition tags correctly — without them a real dimension tag would
	// be mis-bucketed as a read-only attribute. Seeding the picker with only the dimension
	// selection stops the spurious "no axes attached" for classified messages.
	useEffect(() => {
		if (!message || !tags || isAxesLoading || isAttrLoading || form) return
		const { selection, attributes: attrs } = partitionTags(tags, axes)
		setForm(
			fromMessage(
				message,
				selection,
				(objectAttributes ?? []).map((a) => a.value),
			),
		)
		setAttributes(attrs)
	}, [message, tags, axes, isAxesLoading, isAttrLoading, form, objectAttributes])

	if (isLoading || isClassLoading || isAxesLoading || isAttrLoading || !form) return <ListSkeleton label="Loading…" />

	// Read-only "Linked attributes": free-text tags that came through /tags, rendered with
	// their label. `order_code` is NOT among them — it has its own editable field now, and
	// showing it twice would leave the read-only copy stale the moment the field is used.
	const attributeChips = attributes.map((t) => ({ key: `tag:${t.axisCode}:${t.valueCode}`, label: t.label }))

	const onSubmit = () => {
		if (!id) return
		update.mutate(
			{ id, patch: toUpdateInput(form) },
			{
				onSuccess: () => {
					void saveDescriptors(id, form, classify, setOrderCodes, { replaceOrderCodes: true }).then((ok) => {
						if (ok) void navigate(`/messages/${id}`)
					})
				},
			},
		)
	}

	return (
		<div>
			<Link
				to={`/messages/${id}`}
				className="mb-4 inline-flex items-center gap-1 text-sm text-ds-text-muted hover:text-ds-text"
			>
				<ArrowLeft className="h-4 w-4" /> Message
			</Link>
			<PageHeader eyebrow="Messages / Edit" title="Edit message" subtitle="Update any field; changes save on submit." />
			{attributeChips.length > 0 && (
				<div className="mt-6">
					<p className="mb-2 font-mono text-xs uppercase tracking-wide text-ds-text-subtle">Linked attributes</p>
					<div className="flex flex-wrap gap-1.5">
						{attributeChips.map((c) => (
							<Chip key={c.key} variant="muted" size="sm">
								{c.label}
							</Chip>
						))}
					</div>
				</div>
			)}
			<div className="mt-6">
				<MessageForm
					form={form}
					set={set}
					axes={axes}
					orderOptions={orderOptions}
					submitLabel="Save changes"
					isPending={update.isPending || classify.isPending || setOrderCodes.isPending}
					onSubmit={onSubmit}
					onCancel={() => void navigate(`/messages/${id}`)}
				/>
			</div>
		</div>
	)
}
